import React, { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { FiCheckCircle, FiCopy, FiSend } from "react-icons/fi";
import { Page, Field, TextArea, Select, Action, request } from "../MVP/shared";
import { PageHeader } from "../../components/UI";
import { useLanguage } from "../../contexts/LanguageContext";

const CATEGORIES = ["account", "match", "skins", "premium", "tech", "other"];
const CONTEXTS = ["steam", "premium", "match"];

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 720px) 280px;
  gap: 24px;
  align-items: start;
  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 720px);
    .memo {
      order: -1;
    }
  }
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Surface = styled.section`
  display: grid;
  gap: 20px;
  min-width: 0;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 767px) {
    padding: 16px;
  }
`;
const Group = styled.div`
  display: grid;
  gap: 8px;
  min-width: 0;
  label,
  .lbl {
    font-size: 14px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .err {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.danger};
  }
`;
const Memo = styled.aside`
  padding: 20px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  h2 {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 8px;
  }
  ul {
    padding-left: 18px;
    display: grid;
    gap: 6px;
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  p {
    margin-top: 12px;
    padding-top: 12px;
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.warning};
  }
`;
const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  .count {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
  .fail {
    flex-basis: 100%;
    font-size: 14px;
    color: ${(p) => p.theme.colors.danger};
  }
`;
const Done = styled(Surface)`
  justify-items: start;
  h2 {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 22px;
    line-height: 30px;
    svg {
      color: ${(p) => p.theme.colors.success};
    }
  }
  .num {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 15px;
    strong {
      font-size: 20px;
      font-variant-numeric: tabular-nums;
    }
  }
  p {
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .acts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

export default function Support() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const ctx = params.get("context") || "";
  const initialCategory = ctx === "steam" ? "account" : ctx === "premium" ? "premium" : ctx === "match" ? "match" : "";
  const [category, setCategory] = useState(initialCategory);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [withContext, setWithContext] = useState(CONTEXTS.includes(ctx));
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sendError, setSendError] = useState("");
  const [done, setDone] = useState<{ number?: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const lock = useRef(false);
  const refs = { category: useRef<HTMLSelectElement>(null), subject: useRef<HTMLInputElement>(null), body: useRef<HTMLTextAreaElement>(null) };

  /* Ошибки считаются по текущим значениям, но показываются только после первой отправки:
     исправленное поле сразу перестаёт ругаться, а первичный ввод не подсвечивается. */
  const errors: Record<string, string> = {};
  if (!category) errors.category = t("support.err.category");
  if (subject.trim().length < 3) errors.subject = t("support.err.subject");
  if (body.trim().length < 10) errors.body = t("support.err.body");
  const show = (k: string) => (submitted ? errors[k] : undefined);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (lock.current) return;
    setSubmitted(true);
    const first = (["category", "subject", "body"] as const).find((k) => errors[k]);
    if (first) {
      refs[first].current?.focus();
      return;
    }
    lock.current = true;
    setBusy(true);
    setSendError("");
    try {
      const data = await request("/support/tickets", {
        subject: subject.trim(),
        body: body.trim(),
        category,
        context: withContext && CONTEXTS.includes(ctx) ? ctx : "",
      });
      setDone({ number: data?.number !== undefined ? String(data.number) : undefined });
    } catch (err) {
      // Введённый текст сохраняется, ошибка стоит рядом с кнопкой.
      setSendError((err as Error).message || t("support.send_failed"));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }

  function again() {
    setDone(null);
    setSubject("");
    setBody("");
    setCategory("");
    setSubmitted(false);
    setCopied(false);
  }
  async function copy() {
    if (!done?.number) return;
    try {
      await navigator.clipboard.writeText(done.number);
      setCopied(true);
    } catch {
      /* буфер недоступен — номер остаётся на экране */
    }
  }

  return (
    <Page>
      <PageHeader title={t("nav.support")} description={t("support.lead")} />
      {done ? (
        <Done role="status" aria-live="polite">
          <h2>
            <FiCheckCircle aria-hidden="true" />
            {t("support.sent")}
          </h2>
          {done.number && (
            <div className="num">
              {t("support.ticket")}: <strong>#{done.number}</strong>
              <Action type="button" onClick={copy}>
                <FiCopy aria-hidden="true" />
                {copied ? t("support.copied") : t("support.copy")}
              </Action>
            </div>
          )}
          <p>{t("support.after")}</p>
          <div className="acts">
            <Action as={Link} to="/" $variant="primary">
              {t("match.to_home")}
            </Action>
            <Action type="button" onClick={again}>
              {t("support.new")}
            </Action>
          </div>
        </Done>
      ) : (
        <Layout>
          <Surface as="form" onSubmit={submit} noValidate aria-label={t("nav.support")}>
            <Group>
              <label htmlFor="sup-cat">{t("support.category")}</label>
              <Select
                id="sup-cat"
                ref={refs.category}
                value={category}
                aria-invalid={!!show("category")}
                aria-describedby={show("category") ? "sup-cat-err" : undefined}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">{t("support.category_pick")}</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`support.cat.${c}`)}
                  </option>
                ))}
              </Select>
              {show("category") && (
                <span className="err" id="sup-cat-err" role="alert">
                  {show("category")}
                </span>
              )}
            </Group>
            <Group>
              <label htmlFor="sup-sub">{t("support.subject")}</label>
              <Field
                id="sup-sub"
                ref={refs.subject}
                maxLength={200}
                placeholder={t("support.subject_placeholder")}
                value={subject}
                aria-invalid={!!show("subject")}
                aria-describedby={show("subject") ? "sup-sub-err" : undefined}
                onChange={(e) => setSubject(e.target.value)}
              />
              {show("subject") && (
                <span className="err" id="sup-sub-err" role="alert">
                  {show("subject")}
                </span>
              )}
            </Group>
            <Group>
              <label htmlFor="sup-body">{t("support.body")}</label>
              <TextArea
                id="sup-body"
                ref={refs.body}
                maxLength={2000}
                rows={8}
                placeholder={t("support.body_placeholder")}
                value={body}
                aria-invalid={!!show("body")}
                aria-describedby={`sup-count${show("body") ? " sup-body-err" : ""}`}
                onChange={(e) => setBody(e.target.value)}
              />
              {show("body") && (
                <span className="err" id="sup-body-err" role="alert">
                  {show("body")}
                </span>
              )}
            </Group>
            {CONTEXTS.includes(ctx) && (
              <label style={{ display: "flex", gap: 10, alignItems: "center", minHeight: 44 }}>
                <input type="checkbox" checked={withContext} onChange={(e) => setWithContext(e.target.checked)} />
                <span>{t("support.attach_context", { name: t(`support.ctx.${ctx}`) })}</span>
              </label>
            )}
            <Bar>
              <span className="count" id="sup-count">
                {t("support.counter", { n: body.length })}
              </span>
              <Action $variant="primary" type="submit" disabled={busy} aria-busy={busy}>
                {busy ? t("match.sending") : t("support.send")}
                <FiSend aria-hidden="true" />
              </Action>
              {sendError && (
                <span className="fail" role="alert">
                  {sendError}
                </span>
              )}
            </Bar>
          </Surface>
          <Memo className="memo">
            <h2>{t("support.memo_title")}</h2>
            <ul>
              <li>{t("support.memo_1")}</li>
              <li>{t("support.memo_2")}</li>
              <li>{t("support.memo_3")}</li>
            </ul>
            <p>{t("support.no_secrets")}</p>
          </Memo>
        </Layout>
      )}
    </Page>
  );
}
